/**
 * Approximate viewer location, resolved at the CDN edge.
 *
 * CloudFront resolves the viewer's IP against Amazon's own geolocation database
 * and exposes the result as `CloudFront-Viewer-*` request headers. A
 * viewer-response function on the web distribution (see
 * `infrastructure/web.ts`) copies those headers onto the HTML document response
 * as the `fs_geo` cookie, so the browser has a usable location on first paint —
 * no network call, no permission prompt, no third-party vendor.
 *
 * This is deliberately a *seed*, not an answer. IP geolocation returns a
 * centroid: for rural addresses that centroid is usually the ISP's regional
 * hub, so a buyer outside Big Timber routinely resolves to Billings. It is good
 * enough to centre a map and pre-sort by distance; it must never filter
 * listings out of view or drive anything the buyer cannot see and correct.
 * Precise coordinates (`navigator.geolocation`) and a typed address both
 * outrank it — see `useViewerLocation`.
 *
 * Absent locally: `yarn dev` serves Vite directly with no CloudFront in the
 * path, so the cookie never exists. Set `VITE_DEV_VIEWER_GEO` to exercise the
 * seeded path in development.
 */

import { VIEWER_GEO_DEV_OVERRIDE } from 'lib/viewerGeoDevOverride';

/** Cookie written by the viewer-response CloudFront Function. */
export const VIEWER_GEO_COOKIE = 'fs_geo';

export interface ViewerLocation {
  lat: number;
  lng: number;
  /** City name, e.g. "Bozeman". Empty when CloudFront could not resolve one. */
  city: string;
  /** Subdivision code — the state for US viewers, e.g. "MT". */
  region: string;
  /** Two-letter country code, e.g. "US". */
  country: string;
}

/**
 * Wire shape of the cookie. Keys are single characters because CloudFront
 * Functions cap response header size and this rides on every HTML response.
 */
interface ViewerGeoCookiePayload {
  lat?: string | number;
  lng?: string | number;
  c?: string;
  r?: string;
  co?: string;
}

/**
 * Coordinates outside these bounds are a decoding error, not a location.
 * @param lat
 * @param lng
 */
const isPlausibleCoordinate = (lat: number, lng: number) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180 &&
  // CloudFront reports 0/0 when it cannot resolve the IP at all. That is in the
  // Gulf of Guinea, so it is never a real viewer and would silently centre every
  // map on the Atlantic.
  !(lat === 0 && lng === 0);

/**
 * Parse the `fs_geo` cookie payload. Returns null for anything malformed —
 * callers fall back to the precise/typed/default chain rather than surfacing an
 * error, because a missing seed is the normal case (VPN, dev, new CDN region).
 * @param raw
 */
export function parseViewerGeo(
  raw: string | null | undefined,
): ViewerLocation | null {
  if (!raw) return null;

  let payload: ViewerGeoCookiePayload;
  try {
    payload = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }

  if (!payload || typeof payload !== 'object') return null;

  const lat = Number(payload.lat);
  const lng = Number(payload.lng);
  if (!isPlausibleCoordinate(lat, lng)) return null;

  return {
    lat,
    lng,
    city: typeof payload.c === 'string' ? payload.c : '',
    region: typeof payload.r === 'string' ? payload.r : '',
    country: typeof payload.co === 'string' ? payload.co : '',
  };
}

/**
 * Read a cookie value by name from a `document.cookie` string.
 * @param cookieString
 * @param name
 */
export function readCookie(cookieString: string, name: string): string | null {
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim();
  }
  return null;
}

/**
 * Human label for a seeded location — "Bozeman, MT", degrading to the state, the
 * country, then a generic phrase. Used as the `place` param on
 * `/marketplace/search` and as the location chip elsewhere, so it must never be
 * empty (an empty chip reads as a bug).
 * @param location
 */
export function formatViewerLocationLabel(location: ViewerLocation): string {
  const { city, region, country } = location;
  if (city && region) return `${city}, ${region}`;
  if (city) return city;
  if (region) return region;
  if (country) return country;
  return 'Near you';
}

/**
 * Resolve the seed for the current document. Reads the dev override first so
 * `yarn dev` can exercise the seeded path without a CloudFront distribution.
 */
export function getViewerLocation(): ViewerLocation | null {
  if (typeof document === 'undefined') return null;

  if (VIEWER_GEO_DEV_OVERRIDE) {
    const parsed = parseViewerGeo(VIEWER_GEO_DEV_OVERRIDE);
    if (parsed) return parsed;
  }

  return parseViewerGeo(readCookie(document.cookie, VIEWER_GEO_COOKIE));
}
